import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import SortIcon from '@mui/icons-material/Sort';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import { RetryIconButton } from '@/components/common';

import {
  DailyAverageChartContainerHeight,
  EnergyUsageChartContainerHeight,
  ReeferTypeBreakDownChartContainerHeight,
} from '@/components/pages/EmissionsDashboard';

import { useGetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';
import {
  Container_Insights_Bookings_Expanded_Order_By,
  Order_By,
} from '@/graphql/types';

import { useBreakpoints } from '@/hooks';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  getBookingsExpandedQueryWhereClauseFromFilters,
  store,
} from '../store';

import { TripItem } from './TripItem';

function TripList() {
  const { t } = useTranslation();
  const theme = useTheme();
  const snap = useSnapshot(store) as typeof store;
  const appSnap = useSnapshot(appStore);
  const { useDown, useOnly } = useBreakpoints();
  const lgOnly = useOnly('lg');
  const downXl = useDown('xl');

  const [errorId, setErrorId] = useState<string | null>(null);

  const [anchorElSortMenu, setAnchorElSortMenu] = useState<null | HTMLElement>(
    null,
  );

  const [orderByField, setOrderByField] =
    useState<keyof Container_Insights_Bookings_Expanded_Order_By>(
      'end_datetime',
    );

  const [orderByDirection, setOrderByDirection] = useState<Order_By>(
    Order_By.DescNullsLast,
  );

  const handleOpenSortMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorElSortMenu(event.currentTarget);

  const handleCloseSortMenu = () => setAnchorElSortMenu(null);

  const handleChangeDirectionClick = () => {
    setOrderByDirection(
      orderByDirection === Order_By.DescNullsLast
        ? Order_By.AscNullsFirst
        : Order_By.DescNullsLast,
    );
  };

  const handleSetOrderByFieldClick =
    (field: keyof Container_Insights_Bookings_Expanded_Order_By) => () => {
      handleCloseSortMenu();
      setOrderByField(field);
    };

  const where = getBookingsExpandedQueryWhereClauseFromFilters(
    snap as typeof store,
  );

  const [{ data, fetching, error }, reexecuteQuery] =
    useGetBookingsExpandedQuery({
      variables: {
        where,
        order_by: [{ [orderByField]: orderByDirection }],
      },
    });

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
  }, [error, reexecuteQuery]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const bookings = data?.container_insights_bookings_expanded || [];

  /**
   * adapt list size
   */
  let margin = 48;
  if (lgOnly) {
    margin -= 16;
  }

  let containerHeight =
    EnergyUsageChartContainerHeight +
    ReeferTypeBreakDownChartContainerHeight -
    margin;

  if (downXl) {
    containerHeight += DailyAverageChartContainerHeight;
  }

  return (
    <Paper
      variant="elevation"
      sx={{
        p: 2,
        pl: 3,
        width: '100%',
        borderRadius: 4,
      }}
    >
      <Stack
        direction="column"
        sx={{
          pr: 2,
          // maxHeight: `calc(${EnergyUsageChartContainerHeight + ReeferTypeBreakDownChartContainerHeight}px + ${theme.spacing(2)})`,
          overflowY: 'auto',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
            <Typography variant="h5">
              {t('voyages')}{' '}
              <sup
                style={{
                  fontSize: theme.typography.pxToRem(16),
                  color: theme.palette.grey[600],
                  visibility: fetching ? 'hidden' : 'visible',
                }}
              >
                ({bookings.length})
              </sup>
            </Typography>

            <IconButton size="small" onClick={handleChangeDirectionClick}>
              <SwapVertIcon />
            </IconButton>
            <Button
              size="small"
              endIcon={<SortIcon />}
              onClick={handleOpenSortMenu}
            >
              {t('sortBy')}
            </Button>
            <Menu
              open={Boolean(anchorElSortMenu)}
              anchorEl={anchorElSortMenu}
              onClose={handleCloseSortMenu}
            >
              <MenuItem
                dense
                onClick={handleSetOrderByFieldClick('end_datetime')}
              >
                <ListItemIcon
                  sx={{
                    visibility:
                      orderByField === 'end_datetime' ? 'visible' : 'hidden',
                  }}
                >
                  <CheckIcon />
                </ListItemIcon>
                Actual Time of Arrival
              </MenuItem>
              <MenuItem dense onClick={handleSetOrderByFieldClick('co2eq_kg')}>
                <ListItemIcon
                  sx={{
                    visibility:
                      orderByField === 'co2eq_kg' ? 'visible' : 'hidden',
                  }}
                >
                  <CheckIcon />
                </ListItemIcon>
                CO₂e emissions
              </MenuItem>
            </Menu>
          </Stack>

          <Button
            size="small"
            endIcon={<LanguageIcon />}
            sx={{ display: 'none' }}
          >
            View
          </Button>
        </Stack>
        {fetching && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: containerHeight,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!fetching && appSnap.hasRequestError(errorId) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: containerHeight,
            }}
          >
            <Stack direction="column" alignItems="center">
              <Typography color="error">Error while fetching trips</Typography>
              <RetryIconButton onRetry={handleRetry} />
            </Stack>
          </Box>
        )}

        {!fetching && !appSnap.hasRequestError(errorId) && (
          <FixedSizeList
            height={containerHeight}
            itemSize={80}
            itemCount={bookings.length}
            width={'100%'}
            itemKey={(index) => bookings[index].internal_booking_id}
          >
            {({ index, style }) => (
              <TripItem style={style} booking={bookings[index]} />
            )}
          </FixedSizeList>
        )}
      </Stack>
    </Paper>
  );
}

export { TripList };
